import { useState } from "react";
import { ALLERGENS, FOOD_CATEGORIES, MICRONUTRIENTS, PRODUCT_FORMS, PROTEIN_SOURCES, SOURCES } from "./nutrition-model";
import { canReviewNutrition } from "../../../network/service/PlatformAuth";

/**
 * Number input with a unit; highlighted when needed for a grade and empty.
 * `origin` marks a value not from the main match: filled from another entry or calculated.
 */
export function Num({ label, unit, required, step = "any", origin, ...input }) {
  return (
    <label className={`nutri-field${required && input.value === "" ? " nutri-needed" : ""}`}>
      <span>
        {label}
        {origin && input.value !== "" && (
          <em className={`nutri-origin nutri-origin-${origin.kind}`} title={origin.note}>
            {origin.kind === "calc" ? " calculated" : ` ${origin.note}`}
          </em>
        )}
      </span>
      <div className="nutri-unit">
        <input type="number" min="0" step={step} className="form-control" {...input} />
        {unit && <em>{unit}</em>}
      </div>
    </label>
  );
}

/**
 * The nutrition label form. `form` / `set` come from the product editor;
 * `detectedAllergens` marks allergens found in the ingredient text.
 * `hideVerified` hides the "checked" box where an expert approves instead.
 */
function NutritionFields({ form, set, detectedAllergens = [], hideVerified = false, afterIngredients = null }) {
  const [showMore, setShowMore] = useState(false);
  const field = (name) => ({ value: form[name], onChange: (e) => set({ [name]: e.target.value }) });
  const origin = form.origin ?? {};
  // Number fields also show where a value not from the main match came from.
  const numField = (name) => ({ ...field(name), origin: origin[name] });
  const unit = form.isBeverage ? "ml" : "g";
  const microsEntered = Object.values(form.micronutrients).filter((v) => v !== "").length;

  return (
    <div className="nutri-form">
      <fieldset>
        <legend>Nutrition facts per {form.basisGrams || 100} {unit}</legend>
        <div className="nutri-grid">
          <Num label="Energy *" unit="kcal" {...numField("calories")} />
          <Num label="Protein *" unit="g" {...numField("protein")} />
          <Num label="Carbohydrates *" unit="g" {...numField("carbs")} />
          <Num label="Total sugars" unit="g" required {...numField("sugar")} />
          <Num label="Added sugars" unit="g" {...numField("addedSugar")} />
          <Num label="Dietary fibre" unit="g" {...numField("fiber")} />
          <Num label="Total fat *" unit="g" {...numField("fat")} />
          <Num label="Saturated fat" unit="g" required {...numField("saturatedFat")} />
          <Num label="Trans fat" unit="g" {...numField("transFat")} />
          <Num label="Sodium" unit="mg" required {...numField("sodium")} />
          <Num label="Cholesterol" unit="mg" {...numField("cholesterol")} />
          <Num label="One serving / piece" unit={unit} {...field("servingGrams")} />
          <label className="nutri-field">
            <span>Food category</span>
            <select className="form-select" {...field("foodCategory")}>
              {FOOD_CATEGORIES.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>
          <label className="nutri-field">
            <span>How it is sold</span>
            <select className="form-select" {...field("productForm")}>
              {PRODUCT_FORMS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>
        </div>
        <p className="nutri-muted mb-0">
          * required. <span className="nutri-needed">Highlighted</span> fields give a complete health grade;
          without them the grade leaves them out ("Good" at most).
        </p>
      </fieldset>

      <fieldset>
        <legend>
          Vitamins &amp; minerals per {form.basisGrams || 100} {unit}
          {microsEntered > 0 && <span className="nutri-muted"> · {microsEntered} entered</span>}
        </legend>
        <div className="nutri-grid">
          {MICRONUTRIENTS.map(([code, name, u]) => (
            <Num
              key={code}
              label={name}
              unit={u}
              value={form.micronutrients[code] ?? ""}
              origin={origin[`micronutrients.${code}`]}
              onChange={(e) => set({ micronutrients: { ...form.micronutrients, [code]: e.target.value } })}
            />
          ))}
        </div>
        <p className="nutri-muted mb-0">
          Filled automatically from every database entry for this exact food. A packed product takes them only from
          its own label, since fortified packs differ from the plain food. They count towards the grade.
        </p>
      </fieldset>

      <fieldset>
        <legend>Ingredients &amp; allergens</legend>
        <textarea
          className="form-control"
          rows={3}
          placeholder="Copy exactly from the pack, e.g. Refined wheat flour (maida), sugar, palm oil, raising agents [503(ii), 500(ii)], emulsifier (INS 322)"
          {...field("ingredients")}
        />
        <p className="nutri-muted">
          Additives (INS numbers), allergens, processing level and whether sugar was added are read from this text.
        </p>
        {afterIngredients}
        <div className="nutri-allergens">
          <span>Allergens:</span>
          {ALLERGENS.map(([code, name]) => (
            <label key={code} className="nutri-check">
              <input
                type="checkbox"
                checked={form.allergens.includes(code)}
                onChange={(e) =>
                  set({
                    allergens: e.target.checked
                      ? [...form.allergens, code]
                      : form.allergens.filter((a) => a !== code),
                  })
                }
              />
              {name}
              {detectedAllergens.includes(code) && <em className="nutri-muted"> (in ingredients)</em>}
            </label>
          ))}
        </div>
      </fieldset>

      <button type="button" className="nutri-link mb-3" onClick={() => setShowMore(!showMore)}>
        {showMore ? "Hide more details" : "More details (protein, source)"}
      </button>

      {showMore && (
        <>
          <fieldset>
            <legend>Protein &amp; classification</legend>
            <div className="nutri-grid">
              <label className="nutri-field">
                <span>Main protein source</span>
                <select className="form-select" {...field("proteinSource")}>
                  {PROTEIN_SOURCES.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>
              <label className="nutri-field">
                <span>Veg / non-veg</span>
                <select className="form-select" {...field("dietType")}>
                  <option value="">Auto from name</option>
                  <option value="VEG">Veg</option>
                  <option value="EGG">Contains egg</option>
                  <option value="NON_VEG">Non-veg</option>
                </select>
              </label>
              <label className="nutri-field">
                <span>Processing (NOVA)</span>
                <select className="form-select" {...field("novaGroup")}>
                  <option value="">Auto from ingredients</option>
                  <option value="1">1 · Unprocessed</option>
                  <option value="2">2 · Culinary ingredient</option>
                  <option value="3">3 · Processed</option>
                  <option value="4">4 · Ultra-processed</option>
                </select>
              </label>
              <label className="nutri-field">
                <span>Nutri-Score on pack</span>
                <select className="form-select" {...field("nutriScoreDeclared")}>
                  <option value="">None (calculate)</option>
                  {["A", "B", "C", "D", "E"].map((g) => (
                    <option key={g}>{g}</option>
                  ))}
                </select>
              </label>
              <Num label="PDCAAS (if measured, 0-1)" step="0.01" {...field("pdcaas")} />
              <Num label="Fruit / veg / legume" unit="%" {...field("fruitVegLegumePct")} />
              <label className="nutri-field nutri-wide">
                <span>Extra INS numbers not in the ingredients</span>
                <input className="form-control" placeholder="INS 211, INS 330" {...field("extraAdditives")} />
              </label>
              <label className="nutri-check">
                <input type="checkbox" checked={form.isBeverage} onChange={(e) => set({ isBeverage: e.target.checked })} />
                Beverage (values per 100 ml)
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend>Source</legend>
            <div className="nutri-grid">
              <label className="nutri-field">
                <span>Brand</span>
                <input className="form-control" {...field("brandName")} />
              </label>
              <label className="nutri-field">
                <span>Values taken from</span>
                <select className="form-select" {...field("source")}>
                  {SOURCES.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>
              <Num label="Values are per" unit={unit} {...field("basisGrams")} />
            </div>
          </fieldset>
        </>
      )}

      {!hideVerified && canReviewNutrition() && (
        <label className="nutri-check nutri-verified">
          <input type="checkbox" checked={form.isVerified} onChange={(e) => set({ isVerified: e.target.checked })} />
          I checked these values against the product pack
        </label>
      )}
      {!hideVerified && !canReviewNutrition() && (
        <p className="nutri-muted mb-0">
          Customers see the grade after a nutrition expert approves these values in Nutrition Reviews.
        </p>
      )}
    </div>
  );
}

export default NutritionFields;
